import React, { memo, useCallback, Fragment, useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { FlashCardWidgetForm, FlashCardBlockVariation, FlashCardForm } from './FlashCardBlock';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Label, Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { FormSelect, FormColorPicker, FormText, FormInput } from '@core/components/form';
import { CommonService } from '@business/services';
import { openImageFinderModal } from '@modules/ImageFinder';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { SortableList } from '@modules/common/Sortable';
import { ScenarioDrillItem } from './FlashCardBlockItem';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { FormHTML } from '@core/components/form/FormHTML';

export interface ScenarioDrillBlockSettingsProps {
  form: FlashCardWidgetForm;
  variation: FlashCardBlockVariation;
}

export const FlashCardBlockSettings = memo((props: ScenarioDrillBlockSettingsProps) => {
  const { form, variation } = props;
  const scenarios = useObservable(form.controls.flashCards.items$, []);
  const scenarioValues = useObservable(form.controls.flashCards.value$, []);

  useEffect(() => {
    if (scenarios.length === 0) {
      form.controls.flashCards.push();
    }
  }, [form, scenarios]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newScenarios = R.move(scenarioValues, oldIndex, newIndex);
      form.patchValue({
        flashCards: newScenarios,
      });
    },
    [form, scenarioValues],
  );

  return (
    <div>
      <Fragment>
        <Row>
          <Col md={12}>
            <FormHTML control={form.controls.mainInstructions} minimal={true} />
          </Col>
        </Row>
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {scenarios.map((ctrl, index) => {
            return (
              <Card className="bg-light" key={index}>
                <CardTitle>
                  <span className="text-muted ml-2 mt-2" style={{ opacity: 0.5 }}>
                    Card #{index + 1}
                  </span>
                </CardTitle>
                <CardBody>
                  <SortableScenarioItem key={index} index={index} ctrl={ctrl} form={form} i={index} />
                </CardBody>
              </Card>
            );
          })}
        </SortableList>
        <Row>
          <Col md={12}>
            <FormHTML control={form.controls.completionMessage} minimal={true} />
          </Col>
        </Row>
      </Fragment>
    </div>
  );
});

interface ScenarioItemProps {
  i: number;
  ctrl: FlashCardForm;
  form: FlashCardWidgetForm;
}

const ScenarioItem = memo((props: ScenarioItemProps) => {
  const { ctrl, form, i } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical font-size-16 mr-3 mb-3" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <Row key={`question-${i} mb-2 mt-2`}>
        <Col md={10} className="input-width">
          <DragHandle />
          <FormInput control={ctrl.controls.flashCard} />
        </Col>
        <Col md={2}>
          {!i && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.flashCards.push()}>
              Add Card
            </a>
          )}
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.flashCards.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
      </Row>
      <Row key={`details-${i} mt-2`}>
        <Col md={12}>
          <FormInput control={ctrl.controls.flashCardInstructions} />
        </Col>
      </Row>
      <Row key={`details-${i} mt-2`}>
        <Col md={12}>
          <FormHTML control={ctrl.controls.flashCardInstructionDetails} minimal={true} />
        </Col>
      </Row>
      <ScenarioDrillItem control={ctrl} />
      <Row>
        <Col md={12}>
          <FormHTML control={ctrl.controls.incorrectAnswerResponse} minimal={true} />
        </Col>
      </Row>
    </div>
  );
});

export const SortableScenarioItem = SortableElement(ScenarioItem);
